import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationsApi, type Notification } from '@/lib/api'
import { useAuth } from './AuthContext'

interface NotificationContextType {
  notifications: Notification[]
  unreadCount: number
  total: number
  isLoading: boolean
  error: Error | null
  markAsRead: (notificationId: string) => void
  markAllAsRead: () => void
  deleteNotification: (notificationId: string) => void
  refetch: () => void
  loadMore: () => void
  hasMore: boolean
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

const NOTIFICATIONS_LIMIT = 20

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const { isAuthenticated, token } = useAuth()
  const [offset, setOffset] = useState(0)
  const [allNotifications, setAllNotifications] = useState<Notification[]>([])
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fetch notifications
  const { data: notificationsData, isLoading, error, refetch } = useQuery({
    queryKey: ['notifications', offset],
    queryFn: () => notificationsApi.getNotifications({ limit: NOTIFICATIONS_LIMIT, offset }),
    enabled: isAuthenticated,
    refetchInterval: 60000, // Fallback polling every minute
  })

  // Fetch unread count separately for more frequent updates
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationsApi.getUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: 30000, // Every 30 seconds
  })

  // Update all notifications when data changes
  useEffect(() => {
    if (notificationsData?.notifications) {
      if (offset === 0) {
        setAllNotifications(notificationsData.notifications)
      } else {
        setAllNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.notification_id))
          const newNotifications = notificationsData.notifications.filter(
            n => !existingIds.has(n.notification_id)
          )
          return [...prev, ...newNotifications]
        })
      }
    }
  }, [notificationsData, offset])

  // WebSocket connection for real-time updates
  useEffect(() => {
    // Don't connect if not authenticated or token is not a valid string
    if (!isAuthenticated || !token || token === 'undefined' || token.length < 10) return

    const connectWebSocket = () => {
      // Double-check token is still valid before connecting
      if (!token || token === 'undefined' || token.length < 10) {
        console.log('Skipping WebSocket connection - invalid token')
        return
      }

      try {
        const wsUrl = notificationsApi.getWebSocketUrl(token)
        const ws = new WebSocket(wsUrl)

        ws.onopen = () => {
          console.log('Notification WebSocket connected')
        }

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            if (data.type === 'notification') {
              // Add new notification to the list
              setAllNotifications(prev => [data.data, ...prev])
              // Increment unread count
              queryClient.setQueryData(['notifications', 'unread-count'], (old: { count: number } | undefined) => ({
                count: (old?.count || 0) + 1,
              }))

              // Show browser notification if permitted
              if (Notification.permission === 'granted') {
                new Notification(data.data.title, {
                  body: data.data.message,
                  icon: '/logo.png',
                })
              }
            }
          } catch (e) {
            console.error('Failed to parse WebSocket message:', e)
          }
        }

        ws.onclose = () => {
          console.log('Notification WebSocket disconnected')
          // Reconnect after 5 seconds if still authenticated
          if (isAuthenticated && token && token !== 'undefined') {
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000)
          }
        }

        ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          ws.close()
        }

        wsRef.current = ws
      } catch (error) {
        console.error('Failed to connect WebSocket:', error)
      }
    }

    // Small delay to ensure token is fully available
    const connectTimeout = setTimeout(connectWebSocket, 100)

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }

    return () => {
      clearTimeout(connectTimeout)
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [isAuthenticated, token, queryClient])

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: notificationsApi.markAsRead,
    onSuccess: (_, notificationId) => {
      setAllNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        )
      )
      queryClient.setQueryData(['notifications', 'unread-count'], (old: { count: number } | undefined) => ({
        count: Math.max((old?.count || 1) - 1, 0),
      }))
    },
  })

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: notificationsApi.markAllAsRead,
    onSuccess: () => {
      setAllNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })))
      queryClient.setQueryData(['notifications', 'unread-count'], { count: 0 })
    },
  })

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: notificationsApi.deleteNotification,
    onSuccess: (_, notificationId) => {
      const deletedNotification = allNotifications.find(n => n.notification_id === notificationId)
      setAllNotifications(prev => prev.filter(n => n.notification_id !== notificationId))
      if (deletedNotification && !deletedNotification.is_read) {
        queryClient.setQueryData(['notifications', 'unread-count'], (old: { count: number } | undefined) => ({
          count: Math.max((old?.count || 1) - 1, 0),
        }))
      }
    },
  })

  const loadMore = useCallback(() => {
    if (notificationsData && allNotifications.length < notificationsData.total) {
      setOffset(prev => prev + NOTIFICATIONS_LIMIT)
    }
  }, [notificationsData, allNotifications.length])

  const hasMore = notificationsData ? allNotifications.length < notificationsData.total : false

  const value: NotificationContextType = {
    notifications: allNotifications,
    unreadCount: unreadData?.count || 0,
    total: notificationsData?.total || 0,
    isLoading,
    error: error as Error | null,
    markAsRead: (id: string) => markAsReadMutation.mutate(id),
    markAllAsRead: () => markAllAsReadMutation.mutate(),
    deleteNotification: (id: string) => deleteNotificationMutation.mutate(id),
    refetch: () => {
      setOffset(0)
      refetch()
    },
    loadMore,
    hasMore,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}

